import React, { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import Fuse from "fuse.js"
import {
	IconCheckOutline18 as Check,
	IconLinkBrokenOutline18 as LinkBroken,
} from "nucleo-ui-outline-18"
import { FixedSizeList } from "react-window"

import { cn } from "@productlane/lib/src/cn"
import { IssueStatusIcon } from "@productlane/ui"
import { CommandItem } from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import type { SharedFilterItemsProps, WithQuery } from "./types"
import { useThreadsFilterState } from "../threads-filter-state"

export const LinkedIssuesFilterItems: React.FC<
	SharedFilterItemsProps & WithQuery
> = ({ onSelect, query }) => {
	const { issueIds, removeIssue, addIssue } = useThreadsFilterState()

	const [issues] = useQuery(
		queries.issues({ orderBy: "updatedAt", order: "desc" }),
	)

	const filteredIssues = useMemo(() => {
		if (!query)
			return issues.sort(
				(a, b) =>
					new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
			)

		const fuse = new Fuse(issues, {
			keys: [
				{ name: "identifier", weight: 2 },
				{ name: "previousIdentifiers", weight: 1 },
				{ name: "title", weight: 2 },
				{ name: "url", weight: 2 },
				{ name: "publicTitle", weight: 1 },
				{ name: "description", weight: 1 },
				{ name: "publicDescription", weight: 1 },
			],
			threshold: 0.4,
		})
		const results = fuse.search(query)
		return results.map((result) => result.item)
	}, [issues, query])

	return (
		<>
			{!query && (
				<CommandItem
					value="no-linked-issue"
					onSelect={() => {
						if (issueIds.includes(null)) {
							removeIssue(null)
						} else {
							addIssue(null)
						}
						onSelect()
					}}
					key="no-linked-issue"
				>
					<div className="flex w-full items-center gap-2">
						<LinkBroken className="h-4 w-4 flex-none" />
						<span className="flex-1 truncate">No issue linked</span>
						<Check
							className={cn("h-4 w-4 flex-none", {
								"opacity-100": issueIds.includes(null),
								"opacity-0": !issueIds.includes(null),
							})}
						/>
					</div>
				</CommandItem>
			)}
			{filteredIssues.length ? (
				<div className="h-64">
					<FixedSizeList
						height={256}
						width="100%"
						itemCount={filteredIssues.length}
						itemData={filteredIssues}
						itemKey={(index, data) => data[index]?.id ?? index}
						itemSize={48}
						overscanCount={5}
						className="!h-full"
					>
						{({ data, index, style }) => {
							const issue = data[index]

							return !issue ? null : (
								<CommandItem
									value={issue.id}
									onSelect={() => {
										if (issueIds.includes(issue.id)) {
											removeIssue(issue.id)
										} else {
											addIssue(issue.id)
										}
										onSelect()
									}}
									key={issue.id}
									style={style}
								>
									<div className="flex w-full items-center gap-2">
										<IssueStatusIcon
											color={issue.statusColor ?? ""}
											className="h-3.5 w-3.5 shrink-0"
											status={issue.status}
										/>
										<div className="flex-1 truncate">
											<div className="text-secondary">{issue.identifier}</div>
											<div className="truncate">{issue.title}</div>
										</div>
										<Check
											className={cn("h-4 w-4 shrink-0", {
												"opacity-100": issueIds.includes(issue.id),
												"opacity-0": !issueIds.includes(issue.id),
											})}
										/>
									</div>
								</CommandItem>
							)
						}}
					</FixedSizeList>
				</div>
			) : (
				<div className="py-4 text-center text-sm">
					You haven&apos;t linked any issues yet
				</div>
			)}
		</>
	)
}
